<section class="srl">
  <header class="srl-header">
    <div class="srl-header__text">
      <h2 class="srl-title">Active schedules</h2>
      <p class="srl-subtitle">Reports currently set up to deliver automatically.</p>
    </div>
    <button
      type="button"
      class="srl-new-btn"
      (click)="onNewSchedule()"
      [disabled]="loading || saving"
    >
      <span class="srl-new-btn__plus" aria-hidden="true">+</span>
      New schedule
    </button>
  </header>

  @if (loading) {
    <div class="srl-empty">
      <h3 class="srl-empty__title">Loading schedules…</h3>
    </div>
  } @else if (loadError && !schedules.length) {
    <div class="srl-empty">
      <h3 class="srl-empty__title">Could not load schedules</h3>
      <p class="srl-empty__description">{{ loadError }}</p>
      <button type="button" class="srl-new-btn" (click)="loadSchedules()">Retry</button>
    </div>
  } @else if (!schedules.length) {
    <div class="srl-empty">
      <h3 class="srl-empty__title">No scheduled reports yet</h3>
      <p class="srl-empty__description">
        Create a schedule to deliver Shift or Cash reports automatically.
      </p>
    </div>
  } @else {
    <div class="srl-table-wrap">
      <table class="srl-table">
        <thead>
          <tr>
            <th>Report</th>
            <th>Location</th>
            <th>Employee</th>
            <th>Frequency</th>
            <th>Next Run</th>
            <th>Format</th>
            <th>Recipients</th>
            <th>Status</th>
            <th class="srl-table__actions-col">
              <span class="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          @for (schedule of schedules; track trackByScheduleId($index, schedule)) {
            <tr>
              <td>{{ reportLabel(schedule) }}</td>
              <td>{{ schedule.location_label }}</td>
              <td>{{ schedule.employee_label }}</td>
              <td>{{ frequencyLabel(schedule.frequency, schedule.report_type) }}</td>
              <td>{{ schedule.next_run_label || '—' }}</td>
              <td>{{ formatLabel(schedule) }}</td>
              <td class="srl-recipients-cell">
                @if (extraRecipientCount(schedule) > 0) {
                  <span
                    class="srl-recipients srl-recipients--interactive"
                    [matTooltip]="recipientsTooltip(schedule)"
                    matTooltipPosition="below"
                    matTooltipClass="custom-tooltip srl-recipients-tooltip"
                    tabindex="0"
                  >
                    <span class="srl-recipients__primary">{{ primaryRecipient(schedule) }}</span>
                    <span class="srl-recipients__more">+{{ extraRecipientCount(schedule) }}</span>
                  </span>
                } @else {
                  <span class="srl-recipients">
                    <span class="srl-recipients__primary">{{ primaryRecipient(schedule) }}</span>
                  </span>
                }
              </td>
              <td>
                <span
                  class="srl-status"
                  [class.srl-status--active]="schedule.status === 'active'"
                  [class.srl-status--paused]="schedule.status === 'paused'"
                >
                  {{ schedule.status === 'active' ? 'Active' : 'Paused' }}
                </span>
              </td>
              <td class="srl-table__actions-col">
                <div class="srl-menu" data-schedule-menu>
                  <button
                    type="button"
                    class="srl-menu__trigger"
                    [attr.aria-expanded]="openMenuId === schedule.id"
                    aria-label="Schedule actions"
                    [disabled]="saving"
                    (click)="toggleMenu(schedule.id, $event)"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <circle cx="8" cy="3" r="1.25" fill="currentColor" />
                      <circle cx="8" cy="8" r="1.25" fill="currentColor" />
                      <circle cx="8" cy="13" r="1.25" fill="currentColor" />
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  }
</section>

@if (openMenuId && menuPosition) {
  @for (schedule of schedules; track trackByScheduleId($index, schedule)) {
    @if (schedule.id === openMenuId) {
      <div
        class="srl-menu__panel"
        role="menu"
        data-schedule-menu
        [style.top.px]="menuPosition.top"
        [style.right.px]="menuPosition.right"
      >
        <button type="button" class="srl-menu__item" role="menuitem" (click)="onEdit(schedule)">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M11.333 2.667a1.414 1.414 0 0 1 2 2L5.333 12.667 2 13.333l.667-3.333 8.666-7.333Z"
              stroke="currentColor"
              stroke-width="1.3"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          Edit
        </button>
        <button
          type="button"
          class="srl-menu__item"
          role="menuitem"
          (click)="onTogglePause(schedule)"
        >
          @if (schedule.status === 'active') {
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <rect x="4" y="3.5" width="2.5" height="9" rx="0.5" fill="currentColor" />
              <rect x="9.5" y="3.5" width="2.5" height="9" rx="0.5" fill="currentColor" />
            </svg>
            Pause
          } @else {
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M5 3.5v9l7-4.5-7-4.5Z" fill="currentColor" />
            </svg>
            Resume
          }
        </button>
        <button
          type="button"
          class="srl-menu__item srl-menu__item--danger"
          role="menuitem"
          (click)="onDelete(schedule)"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M3.333 4.667h9.334M6 4.667V3.333h4v1.334M6.667 7.333v4M9.333 7.333v4M4.667 4.667l.666 8h5.334l.666-8"
              stroke="currentColor"
              stroke-width="1.3"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          Delete
        </button>
      </div>
    }
  }
}

<app-schedule-report-modal
  [visible]="modalVisible"
  [schedule]="editingSchedule"
  (save)="onModalSave($event)"
  (cancel)="onModalCancel()"
></app-schedule-report-modal>
